import clsx from 'clsx';
import { type ComponentPropsWithoutRef, type ForwardedRef, type ReactNode } from 'react';
import { Button, type ButtonProps, Link, type LinkProps } from 'react-aria-components';

import { Tooltip } from '../Tooltip';
import type { IconDefinition } from '../util';
import { SidebarItemIcon } from './SidebarItem';

export interface SidebarHeadingHookProps {
    /**
     * The type of element to use for the label.
     */
    elementType: 'button' | 'div';

    /**
     * The label to display.
     * This can be a string or a React node.
     */
    label: ReactNode;
    /**
     * The actions to display next to the label.
     * Usually a SidebarHeadingActionButton or SidebarHeadingActionLink.
     */
    actions?: ReactNode;

    /**
     * The class name to apply to the label.
     */
    className?: string;
}

/**
 * Hook to create the props for the sidebar label.
 */
export function useSidebarHeading<T extends SidebarHeadingHookProps>(props: T) {
    const { label, actions, className, elementType, ...rest } = props;
    return {
        labelProps: {
            children: (
                <>
                    <span className="flex flex-1 items-center gap-1 truncate text-ui-small leading-6 font-semibold">
                        {label}
                    </span>
                    {actions}
                </>
            ),
            className: clsx(
                'flex items-center gap-1.5 rounded-sm pl-1.5 text-neutral-low',
                elementType === 'button' &&
                    clsx('hover:bg-standout-side', 'data-[focus-visible]:focus-ring-inset'),
                className
            ),
            ...rest,
        },
    };
}

export interface SidebarHeadingProps
    extends
        Omit<SidebarHeadingHookProps, 'elementType' | 'actions'>,
        ComponentPropsWithoutRef<'div'> {}

/**
 * Display a label in the sidebar.
 */
export function SidebarHeading({
    ref,
    ...props
}: SidebarHeadingProps & { ref?: ForwardedRef<HTMLDivElement> }) {
    const { labelProps } = useSidebarHeading({ ...props, elementType: 'div' });
    return <div ref={ref} {...labelProps} />;
}

export interface SidebarHeadingButtonProps
    extends
        Omit<SidebarHeadingHookProps, 'elementType' | 'actions'>,
        Omit<ButtonProps, 'className'> {
    className?: string;
}

/**
 * Display a label in the sidebar that is a button.
 * Used to create disclosures.
 */
export function SidebarHeadingButton({
    ref,
    ...props
}: SidebarHeadingButtonProps & { ref?: ForwardedRef<HTMLButtonElement> }) {
    const { labelProps } = useSidebarHeading({ ...props, elementType: 'button' });
    return <Button ref={ref} {...labelProps} />;
}

interface SidebarHeadingActionHookProps {
    /**
     * The icon to display.
     */
    icon: IconDefinition;
    /**
     * The label to display.
     */
    label: string;
    /**
     * Optional class name to apply to the action.
     */
    className?: string;
}

/**
 * Hook to create the props for the sidebar label action.
 */
function useSidebarHeadingAction<T extends SidebarHeadingActionHookProps>(props: T) {
    const { icon, label, className, ...rest } = props;
    return {
        actionProps: {
            children: <SidebarItemIcon icon={icon} />,
            className: clsx(
                'opacity-0 group-hover/sidebar-section:opacity-100 focus:opacity-100 aria-expanded:opacity-100',
                'group/sidebar-item flex size-[1.375rem] items-center justify-center rounded-sm',
                'text-base hover:bg-neutral-ui-hover aria-expanded:bg-neutral-ui-active aria-[current=page]:bg-neutral-ui-active data-[pressed=true]:bg-neutral-ui-active',
                'data-[focus-visible]:focus-ring-inset',
                className
            ),
            ...rest,
        },
    };
}

export interface SidebarHeadingActionButtonProps
    extends Omit<ButtonProps, 'children' | 'className'>, SidebarHeadingActionHookProps {}

/**
 * Display an action button in the sidebar label.
 */
export function SidebarHeadingActionButton({
    ref,
    ...props
}: SidebarHeadingActionButtonProps & { ref?: ForwardedRef<HTMLButtonElement> }) {
    const { actionProps } = useSidebarHeadingAction(props);
    return (
        <Tooltip label={props.label}>
            <Button ref={ref} {...actionProps} />
        </Tooltip>
    );
}

export interface SidebarHeadingActionLinkProps
    extends Omit<LinkProps, 'children' | 'className'>, SidebarHeadingActionHookProps {}

/**
 * Display an action link in the sidebar label.
 */
export function SidebarHeadingActionLink({
    ref,
    ...props
}: SidebarHeadingActionLinkProps & { ref?: ForwardedRef<HTMLAnchorElement> }) {
    const { actionProps } = useSidebarHeadingAction(props);
    return (
        <Tooltip label={props.label}>
            <Link ref={ref} {...actionProps} />
        </Tooltip>
    );
}
